import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, Download, Phone, Mail, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import StarScore from "@/components/crm/StarScore";
import {
  statutLabel,
  statutColor,
  bucketOf,
  BUCKET_META,
  formatDate,
  typeAppartLabel,
  localisationLabel,
} from "@/lib/crmHelpers";
import { downloadCSV } from "@/lib/csvExport";

const COLUMNS = [
  { key: "nom", label: "Nom", sortable: true },
  { key: "statut", label: "Statut", sortable: true },
  { key: "bucket", label: "Classement" },
  { key: "score", label: "Score", sortable: true, align: "center" },
  { key: "programme_immobilier", label: "Programme", sortable: true },
  { key: "commercial", label: "Commercial", sortable: true },
  { key: "budget", label: "Budget", sortable: true },
  { key: "derniere_action", label: "Dernier contact", sortable: true },
  { key: "prochaine_action", label: "Relance", sortable: true },
  { key: "actions", label: "", align: "right" },
];

export default function ListView({ leads }) {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState("derniere_action");
  const [sortDir, setSortDir] = useState("desc");

  const sortedLeads = useMemo(() => {
    const copy = [...leads];
    copy.sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      let cmp = 0;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), "fr", { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [leads, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleExport = () => {
    downloadCSV(sortedLeads, `leads_ryad_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="space-y-3" data-testid="list-view">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          <span className="font-heading font-semibold text-foreground">
            {leads.length}
          </span>{" "}
          lead(s) affiché(s)
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          data-testid="export-csv-btn"
          className="gap-2"
        >
          <Download className="h-3.5 w-3.5" />
          Exporter CSV
        </Button>
      </div>

      <div className="scrollbar-thin overflow-x-auto rounded-xl border border-border/60 bg-card">
        {/* min-width forces horizontal scroll instead of squashing columns
            until headers like "Dernier contact" become unreadable. */}
        <table className="w-full min-w-[1100px] text-sm">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30">
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2.5 whitespace-nowrap text-${c.align || "left"} text-label font-semibold text-muted-foreground ${
                    c.sortable ? "cursor-pointer hover:text-foreground" : ""
                  }`}
                  onClick={() => c.sortable && toggleSort(c.key)}
                >
                  <span className="inline-flex items-center gap-1 whitespace-nowrap">
                    {c.label}
                    {c.sortable &&
                      (sortKey === c.key ? (
                        sortDir === "asc" ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedLeads.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-10 text-center text-sm text-muted-foreground"
                >
                  Aucun lead ne correspond aux filtres.
                </td>
              </tr>
            ) : (
              sortedLeads.map((lead) => {
                const b = BUCKET_META[bucketOf(lead)];
                return (
                  <tr
                    key={lead.id}
                    data-testid={`list-row-${lead.id}`}
                    className="cursor-pointer border-b border-border/40 transition-colors hover:bg-muted/40"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-heading font-semibold">{lead.nom}</div>
                      {/* Phone on its own line so the "+221 77 …" digits stay
                          intact and don't wrap mid-number. Email + indicator
                          icon move to a second line below for breathing room
                          and consistent vertical rhythm with adjacent rows. */}
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Phone className="h-3 w-3 flex-none" />
                        <span className="whitespace-nowrap font-mono tabular-nums">
                          {lead.telephone}
                        </span>
                      </div>
                      {lead.email && (
                        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <Mail className="h-3 w-3 flex-none" />
                          <span className="truncate">{lead.email}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statutColor(
                          lead.statut
                        )}`}
                      >
                        {statutLabel(lead.statut)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${b.color}`}
                      >
                        {b.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <StarScore value={lead.score || 0} readOnly size="sm" />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="text-xs">
                        {lead.programme_immobilier || (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {typeAppartLabel(lead.type_appart).toUpperCase()} ·{" "}
                        {localisationLabel(lead.localisation)}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      {lead.commercial || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-medium text-[hsl(var(--accent-gold))]">
                      {lead.budget || (
                        <span className="font-normal text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {lead.derniere_action ? formatDate(lead.derniere_action) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {lead.prochaine_action ? formatDate(lead.prochaine_action) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/leads/${lead.id}`);
                        }}
                      >
                        Ouvrir
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
